import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { toast } from 'Components/DesignSystem/Toast/useToast';
import { AppContext } from 'Context/AppContext';
import { ADAPTERS_COPY, FEED_TABS } from '../Constants';
import { fetchAdapters, saveAssignments } from '../Services/adaptersService';
import {
  addPartyToAdapter,
  buildAssignmentMap,
  buildPartyNameLookup,
  isAssignmentMapDirty,
  removePartyFromAdapter,
  toAssignmentsPayload,
} from '../assignmentState';
import { AdapterRow, AssignmentMap, FeedType, Party } from '../types';

export interface AdaptersContextValue {
  isLoading: boolean;
  loadError: string | null;
  adapters: AdapterRow[];
  parties: Party[];
  feedTypes: FeedType[];
  original: AssignmentMap;
  current: AssignmentMap;
  isEditMode: boolean;
  isDirty: boolean;
  isSaving: boolean;
  partyName: (partyId: string) => string;
  reload: () => Promise<void>;
  startEdit: () => void;
  cancelEdit: () => void;
  /** Resolves `true` when the save landed (or there was nothing to save). */
  save: () => Promise<boolean>;
  addParty: (adapterKey: string, partyId: string) => void;
  removeParty: (adapterKey: string, partyId: string) => void;
}

const AdaptersContext = createContext<AdaptersContextValue | undefined>(undefined);

/**
 * One page-level edit session across every feed tab (ticket § Editing party assignments).
 *
 * `original` is the last saved state, `current` the working copy; `isDirty` compares the two so
 * Save stays disabled until something actually changed and a no-op save can never reach the
 * backend (which would refuse to log it anyway). While the session is dirty the shared
 * `AppContext.editInProgress` guard is armed with this provider's save/cancel, so the sidebar's
 * `UnsavedChangesModal` offers Save / Discard on any navigation attempt.
 *
 * Handlers handed to `AppContext` are built from refs, not closures over render state: the
 * sidebar stores them in its own state at the moment they are set, and a closure would save the
 * assignments as they were then, not as they are when the user clicks Save in the modal.
 */
export const AdaptersProvider = ({ children }: { children: React.ReactNode }) => {
  const appData = useContext(AppContext);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [adapters, setAdapters] = useState<AdapterRow[]>([]);
  const [parties, setParties] = useState<Party[]>([]);
  const [feedTypes, setFeedTypes] = useState<FeedType[]>(FEED_TABS.map((tab) => tab.id));
  const [original, setOriginal] = useState<AssignmentMap>({});
  const [current, setCurrent] = useState<AssignmentMap>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const adaptersRef = useRef(adapters);
  const originalRef = useRef(original);
  const currentRef = useRef(current);
  adaptersRef.current = adapters;
  originalRef.current = original;
  currentRef.current = current;

  const load = useCallback(async () => {
    setIsLoading(true);
    setLoadError(null);
    const result = await fetchAdapters();
    if (result.success) {
      const map = buildAssignmentMap(result.data.adapters);
      setAdapters(result.data.adapters);
      setParties(result.data.parties);
      if (result.data.feed_types?.length) setFeedTypes(result.data.feed_types);
      setOriginal(map);
      setCurrent(map);
    } else {
      setLoadError(result.error);
    }
    setIsLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const isDirty = useMemo(() => isAssignmentMapDirty(original, current), [original, current]);

  const nameLookup = useMemo(() => buildPartyNameLookup(parties, adapters), [parties, adapters]);
  const partyName = useCallback((partyId: string) => nameLookup.get(partyId) ?? partyId, [nameLookup]);

  const startEdit = useCallback(() => setIsEditMode(true), []);

  const cancelEdit = useCallback(() => {
    const wasDirty = isAssignmentMapDirty(originalRef.current, currentRef.current);
    setCurrent(originalRef.current);
    setIsEditMode(false);
    if (wasDirty) toast.info(ADAPTERS_COPY.discardedToast);
  }, []);

  const save = useCallback(async (): Promise<boolean> => {
    if (!isAssignmentMapDirty(originalRef.current, currentRef.current)) {
      setIsEditMode(false);
      return true;
    }
    setIsSaving(true);
    const result = await saveAssignments(toAssignmentsPayload(currentRef.current, adaptersRef.current));
    setIsSaving(false);
    if (!result.success) {
      toast.error(ADAPTERS_COPY.saveFailedToast, { description: result.error });
      return false;
    }
    toast.success(ADAPTERS_COPY.savedToast);
    setIsEditMode(false);
    // Re-read rather than trusting the local picture: the backend refreshes party names and
    // the footer summary, and another admin may have saved in between.
    await load();
    return true;
  }, [load]);

  const addParty = useCallback(
    (adapterKey: string, partyId: string) => setCurrent((prev) => addPartyToAdapter(prev, adapterKey, partyId)),
    [],
  );
  const removeParty = useCallback(
    (adapterKey: string, partyId: string) => setCurrent((prev) => removePartyFromAdapter(prev, adapterKey, partyId)),
    [],
  );

  // Arm the shared navigation guard only while there is something to lose.
  const guardActive = isEditMode && isDirty;
  useEffect(() => {
    if (!guardActive) return undefined;
    appData?.setEditInProgress?.(true);
    appData?.setEditSaveHandler?.(() => save);
    appData?.setEditCancelHandler?.(() => cancelEdit);
    return () => {
      appData?.setEditInProgress?.(false);
      appData?.setEditSaveHandler?.(null);
      appData?.setEditCancelHandler?.(null);
    };
    // appData's setters are stable state dispatchers; re-arming on every appData change would
    // clear and re-set the guard on unrelated toolbar updates.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [guardActive, save, cancelEdit]);

  const value = useMemo<AdaptersContextValue>(
    () => ({
      isLoading,
      loadError,
      adapters,
      parties,
      feedTypes,
      original,
      current,
      isEditMode,
      isDirty,
      isSaving,
      partyName,
      reload: load,
      startEdit,
      cancelEdit,
      save,
      addParty,
      removeParty,
    }),
    [isLoading, loadError, adapters, parties, feedTypes, original, current, isEditMode, isDirty, isSaving,
      partyName, load, startEdit, cancelEdit, save, addParty, removeParty],
  );

  return <AdaptersContext.Provider value={value}>{children}</AdaptersContext.Provider>;
};

export const useAdapters = (): AdaptersContextValue => {
  const context = useContext(AdaptersContext);
  if (!context) {
    throw new Error('useAdapters must be used within an AdaptersProvider');
  }
  return context;
};

export default AdaptersContext;
